import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/layouts/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, Layers, FileText, Users, Award, Bell, TrendingUp, Shield, BarChart3 } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import StatCardEnhanced from '@/components/dashboard/StatCardEnhanced';
import QuickActions from '@/components/dashboard/QuickActions';
import SmartAlerts from '@/components/dashboard/SmartAlerts';
import ActivityTimeline from '@/components/dashboard/ActivityTimeline';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bonusStats, setBonusStats] = useState(null);
  const [insuranceStats, setInsuranceStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchRecentActivities = async () => {
    try {
      const response = await api.get('/audit-logs?limit=15');
      setRecentActivities(response.data.logs || []);
    } catch (error) {
      console.error('Recent activities yüklenemedi:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // İlk yükleme: Sadece temel stats
      const dashRes = await api.get('/dashboard/stats');
      setStats(dashRes.data);
      setLoading(false);
      
      // Arka planda bonus ve sigorta stats yükle (lazy)
      setTimeout(async () => {
        const [bonusRes, insuranceRes] = await Promise.all([
          api.get('/bonus/payouts'),
          api.get('/insurance/reports/summary')
        ]);
        
        const totalPayout = bonusRes.data.reduce((s, p) => s + p.total_bonus, 0);
        setBonusStats({
          total: totalPayout,
          pending: bonusRes.data.filter(p => p.status === 'DRAFT').length,
          approved: bonusRes.data.filter(p => p.status === 'FINAL').length,
          data: bonusRes.data.filter(p => p.status === 'DRAFT')
        });
        
        setInsuranceStats(insuranceRes.data);

        // Bekleyen onayları topla
        fetchPendingApprovals();
        
        // Recent activities (audit logs)
        fetchRecentActivities();
      }, 1000);
      
    } catch (error) {
      console.error('Stats yüklenemedi');
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const [priceLists, bonusPayouts, procurementDocs] = await Promise.all([
        api.get('/price-lists').then(res => res.data.filter(pl => pl.pending_approval)),
        api.get('/bonus/payouts').then(res => res.data.filter(p => p.status === 'DRAFT')),
        api.get('/procurement/shipment/list?status=pending').then(res => res.data.documents || [])
      ]);

      setPendingApprovals({
        priceLists: priceLists || [],
        bonusPayouts: bonusPayouts || [],
        procurementDocs: procurementDocs || [],
        total: (priceLists?.length || 0) + (bonusPayouts?.length || 0) + (procurementDocs?.length || 0)
      });
    } catch (error) {
      console.error('Bekleyen onaylar yüklenemedi:', error);
    }
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* HEADER WITH QUICK ACTIONS */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Gösterge Paneli
            </h1>
            <p className="text-muted-foreground mt-2">Uçar Motor Yönetim Sistemi</p>
          </div>
          
          <QuickActions
            onNewModel={() => navigate('/models')}
            onPublishPriceList={() => navigate('/price-lists')}
            onNewCampaign={() => navigate('/campaigns')}
            onNewUser={() => navigate('/users')}
          />
        </div>
        
        {/* SMART ALERTS */}
        <SmartAlerts
          pendingApprovals={pendingApprovals?.total || 0}
          slaViolations={0}
          achievements={[]}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="overview">Özet</TabsTrigger>
            <TabsTrigger value="executive">Genel Gösterge</TabsTrigger>
            <TabsTrigger value="reports">Detaylı Raporlar</TabsTrigger>
            <TabsTrigger value="approvals">
              Bekleyen Onaylar
              {pendingApprovals && pendingApprovals.total > 0 && (
                <span className="ml-2 bg-orange-500 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                  {pendingApprovals.total}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: ÖZET (Mevcut AdminDashboard içeriği) */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* MAIN CONTENT - 3 COLUMNS */}
              <div className="xl:col-span-3 space-y-6">
                {/* SİSTEM GENELİ - ENHANCED CARDS */}
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">📊 Sistem Geneli</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCardEnhanced
                      title="Markalar"
                      value={stats?.total_brands || 0}
                      icon={Package}
                      color="text-blue-600"
                      bgColor="bg-blue-50"
                      trend={15}
                      link="/brands"
                      sparklineData={[12, 15, 13, 18, 16, 20, 22]}
                    />
                    <StatCardEnhanced
                      title="Modeller"
                      value={stats?.total_models || 0}
                      icon={Layers}
                      color="text-green-600"
                      bgColor="bg-green-50"
                      trend={8}
                      link="/models"
                      sparklineData={[45, 48, 50, 52, 51, 54, 56]}
                    />
                    <StatCardEnhanced
                      title="Aktif Fiyat Listeleri"
                      value={stats?.active_price_lists || 0}
                      icon={FileText}
                      color="text-orange-600"
                      bgColor="bg-orange-50"
                      trend={-5}
                      link="/price-lists"
                      sparklineData={[6, 5, 7, 5, 6, 5, 5]}
                    />
                    <StatCardEnhanced
                      title="Kullanıcılar"
                      value={stats?.total_users || 0}
                      icon={Users}
                      color="text-purple-600"
                      bgColor="bg-purple-50"
                      trend={12}
                      link="/users"
                      sparklineData={[5, 6, 6, 7, 7, 8, 8]}
                    />
                  </div>
                </div>

        {/* BONUS & PRİM SİSTEMİ */}
        {bonusStats && user?.role === 'super_admin' && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-gray-700">💰 Bonus & Prim Sistemi</h2>
            <div className="grid grid-cols-4 gap-6">
            <Card className="p-6 bg-green-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Toplam Prim</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(bonusStats.total)}</p>
                </div>
                <Award className="w-12 h-12 text-green-600" />
              </div>
            </Card>
            <Card className="p-6 bg-orange-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700">Onay Bekleyen</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{bonusStats.pending}</p>
                </div>
                <Bell className="w-12 h-12 text-orange-600" />
              </div>
            </Card>
            <Card className="p-6 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Onaylanan</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{bonusStats.approved}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-blue-600" />
              </div>
            </Card>
            <Card className="p-6 bg-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">Bu Ay Toplam Hedef</p>
                  <p className="text-3xl font-bold text-purple-900 mt-2">{bonusStats.totalTarget || 0}</p>
                  <p className="text-xs text-purple-600 mt-1">{bonusStats.targetCount || 0} danışman</p>
                </div>
                <Award className="w-12 h-12 text-purple-600" />
              </div>
            </Card>
          </div>
          </div>
        )}

        {/* SİGORTA SİSTEMİ */}
        {insuranceStats && user?.role === 'super_admin' && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-gray-700">🛡️ Sigorta Sistemi</h2>
            <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 bg-indigo-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-700">Toplam Üretim</p>
                    <p className="text-3xl font-bold text-indigo-900 mt-2">{formatCurrency(insuranceStats.total_production || 0)}</p>
                  </div>
                  <Shield className="w-12 h-12 text-indigo-600" />
                </div>
              </Card>
              <Card className="p-6 bg-teal-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-teal-700">Onaylanan</p>
                    <p className="text-3xl font-bold text-teal-900 mt-2">{insuranceStats.approved || 0}</p>
                  </div>
                  <Shield className="w-12 h-12 text-teal-600" />
                </div>
              </Card>
              <Card className="p-6 bg-cyan-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-cyan-700">Cevap Oranı</p>
                    <p className="text-3xl font-bold text-cyan-900 mt-2">{insuranceStats.response_rate?.toFixed(1) || 0}%</p>
                  </div>
                  <Shield className="w-12 h-12 text-cyan-600" />
                </div>
              </Card>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Bekleyen</p>
                <p className="text-2xl font-bold">{insuranceStats.pending || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Reddedilen</p>
                <p className="text-2xl font-bold">{insuranceStats.rejected || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Toplam Talep</p>
                <p className="text-2xl font-bold">{insuranceStats.total_requests || 0}</p>
              </Card>
            </div>
          </>
          </div>
        )}

        {/* HIZLI ERİŞİM */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">⚡ Hızlı Erişim</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="/sales-entry" className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:shadow-xl hover:scale-105 transition-all">
            <h3 className="font-bold text-blue-900 text-lg">Satış Girişi</h3>
            <p className="text-sm text-blue-700 mt-2">Aylık satış verilerini gir</p>
          </a>
          <a href="/bonus" className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 hover:shadow-xl hover:scale-105 transition-all">
            <h3 className="font-bold text-green-900 text-lg">Hedef & Prim</h3>
            <p className="text-sm text-green-700 mt-2">Prim sistemi yönetimi</p>
          </a>
          <a href="/performance" className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200 hover:shadow-xl hover:scale-105 transition-all">
            <h3 className="font-bold text-purple-900 text-lg">Performans & Onay</h3>
            <p className="text-sm text-purple-700 mt-2">Prim onay ve raporlar</p>
          </a>
          </div>
        </div>
              </div>
              
              {/* ACTIVITY TIMELINE - RIGHT SIDEBAR */}
              <div className="xl:col-span-1">
                <ActivityTimeline activities={recentActivities} limit={15} />
              </div>
            </div>
          </TabsContent>

          {/* TAB 2: GENEL GÖSTERGE (ExecutiveDashboard içeriği) */}
          <TabsContent value="executive" className="mt-6">
            <ExecutiveDashboardContent />
          </TabsContent>

          {/* TAB 3: DETAYLI RAPORLAR (Reports içeriği) */}
          <TabsContent value="reports" className="mt-6">
            <ReportsContent />
          </TabsContent>

          {/* TAB 4: BEKLEYEN ONAYLAR */}
          <TabsContent value="approvals" className="space-y-6 mt-6">
            <ApprovalsTab 
              pendingApprovals={pendingApprovals}
              user={user}
              onApprovalAction={fetchPendingApprovals}
            />
          </TabsContent>

        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// ExecutiveDashboard içeriğini component olarak çıkar
function ExecutiveDashboardContent() {
  const navigate = require('react-router-dom').useNavigate();
  const [preorderStats, setPreorderStats] = useState(null);
  const [refundStats, setRefundStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [insuranceStats, setInsuranceStats] = useState(null);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      const [preorder, refund, task, insurance] = await Promise.all([
        api.get('/preorders/reports/summary'),
        api.get('/refunds/reports/summary'),
        api.get('/tasks/reports/summary'),
        api.get('/insurance/reports/sla')
      ]);
      setPreorderStats(preorder.data);
      setRefundStats(refund.data);
      setTaskStats(task.data);
      setInsuranceStats(insurance.data);
    } catch (error) {
      console.error('İstatistikler yüklenemedi');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Dikkat Çekenler */}
      <Card className="p-6 bg-orange-50 border-orange-300">
        <h2 className="text-xl font-semibold mb-4 text-orange-900">⚠️ Dikkat Çekenler</h2>
        <div className="space-y-2">
          {taskStats?.late > 0 && (
            <p className="text-sm text-orange-800 cursor-pointer hover:underline" onClick={() => navigate('/tasks')}>
              🔴 {taskStats.late} görev gecikti
            </p>
          )}
          {refundStats?.total_requests > 0 && (
            <p className="text-sm text-orange-800 cursor-pointer hover:underline" onClick={() => navigate('/refunds')}>
              🟠 {refundStats.total_requests} iade talebi beklemede
            </p>
          )}
          {preorderStats?.total_orders - preorderStats?.completed > 5 && (
            <p className="text-sm text-orange-800 cursor-pointer hover:underline" onClick={() => navigate('/preorders')}>
              🟡 {preorderStats.total_orders - preorderStats.completed} ön sipariş açık
            </p>
          )}
        </div>
      </Card>

      {/* KPI Kartları */}
      <div>
        <h2 className="text-lg font-semibold mb-4 text-gray-700">📊 Operasyonel Özet</h2>
        <div className="grid grid-cols-4 gap-6">
          <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/preorders')}>
            <p className="text-sm text-muted-foreground">Açık Ön Sipariş</p>
            <p className="text-4xl font-bold mt-2">{preorderStats?.total_orders || 0}</p>
            <p className="text-xs text-green-600 mt-2">Kapora: {formatCurrency(preorderStats?.total_deposit || 0)}</p>
          </Card>
          
          <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/refunds')}>
            <p className="text-sm text-muted-foreground">İade Talepleri</p>
            <p className="text-4xl font-bold mt-2">{refundStats?.total_requests || 0}</p>
          </Card>

          <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/tasks')}>
            <p className="text-sm text-muted-foreground">Açık Görevler</p>
            <p className="text-4xl font-bold mt-2">{taskStats?.open || 0}</p>
            <p className="text-xs text-red-600 mt-2">Geciken: {taskStats?.late || 0}</p>
          </Card>

          <Card className="p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/insurance')}>
            <p className="text-sm text-muted-foreground">Sigorta SLA</p>
            <p className="text-4xl font-bold mt-2">{insuranceStats?.sla_compliance_rate ? `${insuranceStats.sla_compliance_rate}%` : '0%'}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ============ BEKLEYEN ONAYLAR TAB ============
function ApprovalsTab({ pendingApprovals, user, onApprovalAction }) {
  const [approving, setApproving] = useState(false);

  if (!pendingApprovals) {
    return (
      <Card className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-muted-foreground mt-4">Onaylar yükleniyor...</p>
      </Card>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value);
  };

  const handleApprove = async (type, id) => {
    setApproving(true);
    try {
      if (type === 'bonus') {
        await api.post(`/bonus/payouts/${id}/approve`);
        toast.success('Prim onaylandı');
      } else if (type === 'priceList') {
        await api.post(`/price-lists/${id}/approve-and-publish`);
        toast.success('Fiyat listesi onaylandı');
      } else if (type === 'procurement') {
        await api.put(`/procurement/shipment/${id}/approve`);
        toast.success('Sevkiyat formu onaylandı');
      }
      onApprovalAction();
    } catch (error) {
      toast.error('Onaylama başarısız');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (type, id) => {
    if (!window.confirm('Bu onayı reddetmek istediğinizden emin misiniz?')) return;
    
    setApproving(true);
    try {
      if (type === 'bonus') {
        await api.delete(`/bonus/payouts/${id}`);
        toast.success('Prim reddedildi');
      } else if (type === 'priceList') {
        await api.post(`/price-lists/${id}/reject`);
        toast.success('Fiyat listesi reddedildi');
      }
      onApprovalAction();
    } catch (error) {
      toast.error('Red işlemi başarısız');
    } finally {
      setApproving(false);
    }
  };

  const totalPending = pendingApprovals.total;

  return (
    <div className="space-y-6">
      {/* Özet Kartı */}
      <Card className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-orange-900">Bekleyen Onaylar</h2>
            <p className="text-sm text-orange-700 mt-1">Onayınız bekleniyor</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold text-orange-900">{totalPending}</p>
            <p className="text-sm text-orange-700">Toplam</p>
          </div>
        </div>
      </Card>

      {totalPending === 0 && (
        <Card className="p-12 text-center">
          <p className="text-2xl">✅</p>
          <p className="text-xl font-semibold text-green-700 mt-4">Tüm Onaylar Tamamlandı!</p>
          <p className="text-sm text-muted-foreground mt-2">Şu an bekleyen onay bulunmuyor</p>
        </Card>
      )}

      {/* Prim Onayları */}
      {pendingApprovals.bonusPayouts.length > 0 && (
        <Card className="p-6 border-2 border-green-300">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-green-900">
            <Award className="w-6 h-6 mr-2" />
            Prim Onayları ({pendingApprovals.bonusPayouts.length})
          </h3>
          <div className="space-y-3">
            {pendingApprovals.bonusPayouts.map((payout) => (
              <div key={payout.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-lg">{payout.rep_name || 'Danışman'}</p>
                    <p className="text-sm text-muted-foreground">
                      Başarı: {payout.overall_pct?.toFixed(1)}% | Prim: {formatCurrency(payout.total_bonus)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {payout.month}/{payout.year} dönemi
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReject('bonus', payout.id)}
                      disabled={approving}
                    >
                      Reddet
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove('bonus', payout.id)}
                      disabled={approving}
                    >
                      Onayla
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Fiyat Listesi Onayları */}
      {pendingApprovals.priceLists.length > 0 && (
        <Card className="p-6 border-2 border-blue-300">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-blue-900">
            <FileText className="w-6 h-6 mr-2" />
            Fiyat Listesi Onayları ({pendingApprovals.priceLists.length})
          </h3>
          <div className="space-y-3">
            {pendingApprovals.priceLists.map((priceList) => (
              <div key={priceList.id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-lg">
                      {priceList.brand_name} - {priceList.month}/{priceList.year} v{priceList.version}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {priceList.items?.length || 0} model içeriyor
                    </p>
                    {priceList.change_log && priceList.change_log.length > 0 && (
                      <p className="text-xs text-blue-700 mt-1">
                        Değişiklik: {priceList.change_log[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReject('priceList', priceList.id)}
                      disabled={approving}
                    >
                      Reddet
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleApprove('priceList', priceList.id)}
                      disabled={approving}
                    >
                      Onayla & Yayınla
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sevkiyat Formu Onayları */}
      {pendingApprovals.procurementDocs.length > 0 && (
        <Card className="p-6 border-2 border-purple-300">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-purple-900">
            <Package className="w-6 h-6 mr-2" />
            Sevkiyat Formu Onayları ({pendingApprovals.procurementDocs.length})
          </h3>
          <div className="space-y-3">
            {pendingApprovals.procurementDocs.map((doc) => (
              <div key={doc.id} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-lg">{doc.document_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Tedarikçi: {doc.supplier} | {doc.items_count} ürün
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Yüklenme: {new Date(doc.uploaded_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={() => handleApprove('procurement', doc.id)}
                      disabled={approving}
                    >
                      Onayla
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


// Reports içeriğini component olarak çıkar
function ReportsContent() {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">📋 Detaylı Raporlar</h2>
        <p className="text-muted-foreground">Rapor modülleri burada gösterilecek (CRM, Satış, vs.)</p>
      </Card>
    </div>
  );
}
